import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import API from "../config/api.js";

const CanvaContext = createContext(null);

const POLL_INITIAL_DELAY = 15000; // 15s before first status check
const POLL_RETRY_DELAY   = 10000; // 10s between checks while pending
const MAX_POLL_ATTEMPTS  = 60;    // ~10min max per job
const COOKIE_PREFIX      = "canva_jobs_user_";

const log = (...args) => console.log("%c[Canva]", "color: #00C4CC; font-weight: bold", ...args);

// ── helpers ──────────────────────────────────────────────────────────────
function getCookie(name) {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function CanvaProvider({ children }) {
  const [connected, setConnected]         = useState(false);
  const [canvaUsername, setCanvaUsername] = useState("");
  const [designs, setDesigns]             = useState([]);
  const [continuation, setContinuation]   = useState("");
  const [hasMore, setHasMore]             = useState(true);
  const [loading, setLoading]             = useState(false);
  const [jobsRunning, setJobsRunning]     = useState(false);
  const [panelOpen, setPanelOpen]         = useState(false);

  const userHashRef         = useRef("");
  const onJobCompletedRef   = useRef(null);
  const draftPromptJobsRef  = useRef(new Set());
  const activeJobsRef       = useRef(new Set());

  // ── init ─────────────────────────────────────────────────────────────────
  // user.iduser is already an md5 hash from the backend.
  const initCanva = useCallback((user) => {
    if (!user) return;
    userHashRef.current = String(user.iduser);
    log("initCanva: userHash =", userHashRef.current);

    const expiration = user.canvausertokenexpiration;
    const isConnected = !!expiration && Number(expiration) * 1000 > Date.now();

    setConnected(isConnected);
    setCanvaUsername(user.canvausername || "");
  }, []);

  // ── list designs ──────────────────────────────────────────────────────────
  const fetchDesigns = useCallback(async (continuationToken = "", reset = false) => {
    if (loading) return;
    setLoading(true);

    try {
      const fd = new FormData();
      fd.append("continuation", continuationToken);

      const res  = await fetch(`${API.canva}?listDesigns`, { method: "POST", credentials: "include", body: fd });
      const data = await res.json();

      const items = Array.isArray(data?.items) ? data.items : [];
      const next  = data?.continuation || "";

      if (reset) setDesigns(items);
      else setDesigns((prev) => [...prev, ...items]);

      setContinuation(next);
      setHasMore(!!next);
    } catch (err) {
      console.error("Canva fetchDesigns error:", err);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [loading]);

  const loadMore = useCallback(() => {
    if (hasMore && !loading) fetchDesigns(continuation);
  }, [hasMore, loading, continuation, fetchDesigns]);

  const refreshDesigns = useCallback(() => {
    setDesigns([]);
    setContinuation("");
    setHasMore(true);
    fetchDesigns("", true);
  }, [fetchDesigns]);

  // ── status check (single call to PHP) ─────────────────────────────────────
  const checkJobStatus = useCallback(async (canvajobid) => {
    log("checkJobStatus: requesting", canvajobid);
    try {
      const fd = new FormData();
      fd.append("canvajobid", canvajobid);

      const res  = await fetch(`${API.canva}?importCanvaDesign`, { method: "POST", credentials: "include", body: fd });
      const text = await res.text();
      log("checkJobStatus: raw =", text);

      let data;
      try { data = JSON.parse(text); }
      catch { return "pending"; }

      if (data.status === "completed") {
        const id   = data.idimage || data.idreel;
        const type = data.idimage ? "image" : "reel";
        log("checkJobStatus: COMPLETED", { type, id });

        if (onJobCompletedRef.current) {
          onJobCompletedRef.current(type, id);
        } else if (draftPromptJobsRef.current.has(canvajobid)) {
          const url = type === "image"
            ? `upload/image/${id}/image/original.jpg`
            : `upload/reel/${id}/file/file.mp4`;
          window._draft_prompt_item_add?.(type, id, url);
        }

        draftPromptJobsRef.current.delete(canvajobid);
        return "completed";
      }

      if (data.status === "error") return "error";
      return "pending";
    } catch (err) {
      console.error("Canva checkJobStatus error:", err);
      return "pending";
    }
  }, []);

  // ── poll a single job until it finishes ───────────────────────────────────
  const pollJob = useCallback(async (canvajobid) => {
    if (!canvajobid) return;
    if (activeJobsRef.current.has(canvajobid)) {
      log("pollJob: already polling, skipping", canvajobid);
      return;
    }
    activeJobsRef.current.add(canvajobid);
    setJobsRunning(true);

    log("pollJob: STARTED", canvajobid, `— waiting ${POLL_INITIAL_DELAY}ms`);
    await sleep(POLL_INITIAL_DELAY);

    let attempts = 0;
    while (attempts < MAX_POLL_ATTEMPTS) {
      attempts++;
      log(`pollJob: attempt ${attempts}/${MAX_POLL_ATTEMPTS} for ${canvajobid}`);

      const status = await checkJobStatus(canvajobid);
      log(`pollJob: attempt ${attempts} → ${status}`);

      if (status === "completed" || status === "error") {
        log(`pollJob: FINISHED ${canvajobid} with`, status);
        break;
      }

      log(`pollJob: still pending, waiting ${POLL_RETRY_DELAY}ms`);
      await sleep(POLL_RETRY_DELAY);
    }

    activeJobsRef.current.delete(canvajobid);
    log("pollJob: removed from active. Remaining:", activeJobsRef.current.size);

    if (activeJobsRef.current.size === 0) {
      setJobsRunning(false);
    }
  }, [checkJobStatus]);

  const pollJobRef = useRef(null);
  useEffect(() => { pollJobRef.current = pollJob; }, [pollJob]);

  // ── fallback: pick up jobs from cookie (previous sessions, same-domain) ──
  const runJobsFromCookie = useCallback(() => {
    if (!userHashRef.current) return;

    const cookieKey = COOKIE_PREFIX + userHashRef.current;
    const raw = getCookie(cookieKey);
    if (!raw) {
      log("runJobsFromCookie: no cookie (fine if no pending jobs)");
      return;
    }

    let jobs = [];
    try { jobs = JSON.parse(raw) || []; }
    catch { return; }

    log("runJobsFromCookie: found jobs =", jobs);
    jobs.forEach((canvajobid) => pollJobRef.current?.(canvajobid));
  }, []);

  // ── export design ─────────────────────────────────────────────────────────
  const exportDesign = useCallback(async (type, canvadesignid, draftPrompt = false) => {
    log("exportDesign: called", { type, canvadesignid, draftPrompt });
    try {
      const fd = new FormData();
      fd.append("type", type);
      fd.append("canvadesignid", canvadesignid);
      fd.append("draftPrompt", draftPrompt ? "1" : "0");

      const res  = await fetch(`${API.canva}?addCanvaDesign`, { method: "POST", credentials: "include", body: fd });
      const text = await res.text();
      log("exportDesign: raw response =", text);

      let data;
      try { data = JSON.parse(text); }
      catch { data = text; }

      const isSuccess = data === true || data === "true" || data?.status === "success";

      if (!isSuccess) {
        log("exportDesign: response is NOT success", data);
        return;
      }

      log("exportDesign: SUCCESS");

      // mark this design as exported in the UI
      setDesigns((prev) =>
        prev.map((d) =>
          d.id === canvadesignid
            ? { ...d, [`exported_${type}`]: true }
            : d
        )
      );

      // PRIMARY PATH: jobid comes directly from the JSON response
      const jobId = data?.canvajobid || data?.jobid || data?.job_id;

      if (jobId) {
        log("exportDesign: jobId from response =", jobId);
        if (draftPrompt) draftPromptJobsRef.current.add(jobId);
        pollJobRef.current?.(jobId);
        return;
      }

      // FALLBACK: try cookie (backward compat / same-domain in production)
      log("exportDesign: no jobId in response, trying cookie");
      setTimeout(() => {
        const cookieKey = COOKIE_PREFIX + userHashRef.current;
        const raw = getCookie(cookieKey);
        log("exportDesign: cookie =", raw);

        if (!raw) {
          log("exportDesign: ⚠️ no jobId in response AND no cookie — update PHP to return canvajobid");
          return;
        }

        try {
          const jobs = JSON.parse(raw) || [];
          if (draftPrompt) jobs.forEach((id) => draftPromptJobsRef.current.add(id));
          jobs.forEach((id) => pollJobRef.current?.(id));
        } catch (_) {}
      }, 300);
    } catch (err) {
      console.error("Canva exportDesign error:", err);
    }
  }, []);

  // on mount: pick up pending jobs from cookie (useful in production / same-domain)
  useEffect(() => {
    const t = setTimeout(() => {
      log("mount: checking cookie for pending jobs");
      runJobsFromCookie();
    }, 1000);
    return () => clearTimeout(t);
  }, [runJobsFromCookie]);

  // ── panel ─────────────────────────────────────────────────────────────────
  const openCanva = useCallback(({ onCompleted } = {}) => {
    onJobCompletedRef.current = onCompleted || null;
    setPanelOpen(true);
  }, []);

  const closeCanva = useCallback(() => {
    setPanelOpen(false);
  }, []);

  // ── window debug ──────────────────────────────────────────────────────────
  useEffect(() => {
    window._canva = {
      open:    (onCompleted) => openCanva({ onCompleted }),
      refresh: ()            => refreshDesigns(),
      export:  (type, id)    => exportDesign(type, id),
      poll:    (jobid)       => pollJobRef.current?.(jobid),
      debug:   ()            => ({
        userHash: userHashRef.current,
        cookie:   getCookie(COOKIE_PREFIX + userHashRef.current),
        active:   Array.from(activeJobsRef.current),
      }),
    };
    return () => { delete window._canva; };
  }, [openCanva, refreshDesigns, exportDesign]);

  return (
    <CanvaContext.Provider
      value={{
        connected,
        canvaUsername,
        initCanva,
        designs,
        loading,
        hasMore,
        loadMore,
        refreshDesigns,
        fetchDesigns,
        jobsRunning,
        exportDesign,
        onJobCompletedRef,
        panelOpen,
        openCanva,
        closeCanva,
      }}
    >
      {children}

      {jobsRunning && (
        <div
          className="position-fixed top-0 end-0 m-5 me-4"
          style={{ zIndex: 1100 }}
        >
          <div className="mt-3 p-3 bg-white text-dark rounded-3 shadow">
            <h6 className="text-center title mb-2">
              Importing From{" "}
              <img src="/assets/img/canva.svg" height="20" alt="Canva" />
              <br />
              <small className="text-muted" style={{ fontSize: 12 }}>
                Hang tight while we convert your design!
              </small>
            </h6>
            <div className="progress">
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                aria-valuenow={75}
                aria-valuemin={0}
                aria-valuemax={100}
                style={{ width: "75%" }}
              />
            </div>
          </div>
        </div>
      )}
    </CanvaContext.Provider>
  );
}

export function useCanva() {
  return useContext(CanvaContext);
}